import { ChatFeedbacks } from 'containers/AIQuery/containers/AIQueryChatContainer/components/MessageContainer/Feedback/chatFeedbacks.query';
import { EvidenceFeedbackQuery } from 'containers/components/insights/components/EvidenceFeedback/evidenceFeedback.query';
import { UnmoderatedBooking } from 'containers/Studies/data/UnmoderatedBooking.query';
import { FetchProjectById } from 'data/queries/project/fetchProjectById.query';
import { invalidateFocusGroupSessions } from 'containers/FocusGroups/data/focusGroupSessionsCache';
import { annotationMutationUpdates } from 'containers/Transcript/annotations/data/annotationMutationUpdates';
import { tagMutationUpdates } from 'containers/Tags/data/tagMutationUpdates';

import {
  invalidateBookingAndCredits,
  invalidateBookingById,
  invalidateQueryField,
  invalidateQueryFields,
  invalidateRemovedByoSubmissions,
  invalidateResultsAggregations,
} from './invalidations';

import type { CreateProject } from 'components/askablePlus/data/CreateProject.mutation';
import type { ForceCloseBooking } from 'components/manageBooking/updateBooking/data/ForceCloseBooking.mutation';
import type { RefundBookingUpdated } from 'components/manageBooking/updateBooking/data/RefundBookingUpdated.mutation';
import type { ReOpenBooking } from 'components/manageBooking/updateBooking/data/ReopenBooking.mutation';
import type { CreateChatFeedback } from 'containers/AIQuery/containers/AIQueryChatContainer/components/MessageContainer/Feedback/CreateChatFeedback.mutation';
import type { ChatFeedbackData } from 'containers/AIQuery/containers/AIQueryChatContainer/components/MessageContainer/Feedback/types';
import type { CreateEvidenceFeedback } from 'containers/components/insights/components/EvidenceFeedback/createEvidenceFeedback.mutation';
import type { DeleteChat } from 'containers/AIQuery/containers/AIQueryLandingContainer/data/DeleteChat.mutation';
import type { DeleteByoSubmissions } from 'containers/Booking/containers/BookingParticipantsContainer/data/DeleteByoSubmissions.mutation';
import type { RemoveByoSubmissionsFromResults } from 'containers/Booking/containers/BookingParticipantsContainer/data/RemoveByoSubmissionsFromResults.mutation';
import type { SelfScheduledInvite } from 'containers/Booking/containers/BookingParticipantsContainer/components/BookingParticipantsSidePanel/queries/SelfScheduledInvite.mutation';
import type { UpdateBookingSegments } from 'containers/Studies/data/UpdateBookingSegments.mutation';
import type { UpdateStudyRecordingSettings } from 'containers/Studies/data/UpdateStudyRecordingSettings.mutation';
import type {
  AssignFocusGroupFacilitator,
  CreateFocusGroupSessions,
  DeleteFocusGroupSession,
} from 'containers/FocusGroups/data/FocusGroupMutations';
import type { CreateSessionsLiveBooking } from 'data/mutations/booking/createSessionsLiveBooking.mutation';
import type { RescheduleSessionLiveBooking } from 'data/mutations/booking/rescheduleSessionLiveBooking.mutation';
import type { ReInviteParticipantSession } from 'data/mutations/bookingSubmission/reInviteParticipantSession.mutation';
import type { RevokeParticipantInvitation } from 'data/mutations/bookingSubmission/revokeParticipantInvitation.mutation';

import type { Cache } from '@urql/exchange-graphcache';
import type { ResultOf, VariablesOf } from 'gql.tada';

export const mutationUpdates = {
  createChatFeedback: (
    result: ResultOf<typeof CreateChatFeedback>,
    args: VariablesOf<typeof CreateChatFeedback>,
    cache: Cache,
  ) => {
    // Update every cached chatFeedbacks query that includes this chatMessageId.
    cache.inspectFields('Query').forEach(fieldInfo => {
      if (fieldInfo.fieldName !== 'chatFeedbacks') return;
      const cachedVariables = fieldInfo.arguments as { chatMessageIds: string[] };
      if (!cachedVariables?.chatMessageIds?.includes(args.input.chatMessageId)) return;

      cache.updateQuery<ResultOf<typeof ChatFeedbacks>, VariablesOf<typeof ChatFeedbacks>>(
        { query: ChatFeedbacks, variables: cachedVariables },
        data => {
          if (!data?.chatFeedbacks) return data;

          const existingIndex = data.chatFeedbacks.findIndex(
            feedback => feedback.chatMessageId === args.input.chatMessageId,
          );

          // userId/teamId are filled in by the server response on next fetch.
          const newFeedback: ChatFeedbackData = {
            ...result.createChatFeedback,
            chatMessageId: args.input.chatMessageId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            userId: '',
            teamId: '',
          };

          if (existingIndex >= 0) {
            const updatedFeedbacks = [...data.chatFeedbacks];
            updatedFeedbacks[existingIndex] = newFeedback;
            return { ...data, chatFeedbacks: updatedFeedbacks };
          }
          return { ...data, chatFeedbacks: [...data.chatFeedbacks, newFeedback] };
        },
      );
    });
  },

  createEvidenceFeedback: (
    result: ResultOf<typeof CreateEvidenceFeedback>,
    args: VariablesOf<typeof CreateEvidenceFeedback>,
    cache: Cache,
  ) => {
    const feedback = result.createEvidenceFeedback;
    if (!feedback) return;

    // Write the new rating into the cached evidenceFeedback(evidenceId, chatMessageId)
    // query so the thumb reflects it immediately. The query starts null (unrated),
    // so graphcache can't link the created entity to it without this.
    cache.updateQuery<ResultOf<typeof EvidenceFeedbackQuery>, VariablesOf<typeof EvidenceFeedbackQuery>>(
      {
        query: EvidenceFeedbackQuery,
        variables: { evidenceId: args.input.evidenceId, chatMessageId: args.input.chatMessageId },
      },
      () => ({ evidenceFeedback: feedback }),
    );
  },

  deleteChat: (result: ResultOf<typeof DeleteChat>, _args: VariablesOf<typeof DeleteChat>, cache: Cache) => {
    // Invalidate the deleted chat so every query that includes it refetches.
    if (result.deleteChat?.id) {
      cache.invalidate({ __typename: 'Chat', id: result.deleteChat.id });
    }
  },

  excludeFromResults: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateResultsAggregations(cache);
  },

  includeInResults: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateResultsAggregations(cache);
  },

  deleteByoSubmissions: (result: ResultOf<typeof DeleteByoSubmissions>, _args: unknown, cache: Cache) => {
    invalidateRemovedByoSubmissions(cache, result.deleteByoSubmissions);
  },

  // Hides the response instead of deleting it, so the results stop counting it.
  removeByoSubmissionsFromResults: (
    result: ResultOf<typeof RemoveByoSubmissionsFromResults>,
    _args: unknown,
    cache: Cache,
  ) => {
    invalidateRemovedByoSubmissions(cache, result.removeByoSubmissionsFromResults);
  },

  updateStudyRecordingSettings: (
    result: ResultOf<typeof UpdateStudyRecordingSettings>,
    args: VariablesOf<typeof UpdateStudyRecordingSettings>,
    cache: Cache,
  ) => {
    const updatedUnmoderated = result.updateStudyRecordingSettings?.config?.unmoderated;
    if (!updatedUnmoderated) return;

    cache.updateQuery({ query: UnmoderatedBooking, variables: { id: args.input._id } }, data => {
      if (!data?.unmoderatedBooking?.config?.unmoderated) return data;
      return {
        ...data,
        unmoderatedBooking: {
          ...data.unmoderatedBooking,
          config: {
            ...data.unmoderatedBooking.config,
            unmoderated: {
              ...data.unmoderatedBooking.config.unmoderated,
              recording_options: updatedUnmoderated.recording_options,
              task_blocks: updatedUnmoderated.task_blocks,
            },
          },
        },
      };
    });
  },

  createProject: (result: ResultOf<typeof CreateProject>, _args: VariablesOf<typeof CreateProject>, cache: Cache) => {
    // Seed FetchProjectById so the post-redirect useQuery gets an immediate cache
    // hit. Without it a refetch can race backend consistency and land
    // project={null}, bouncing users back to /askable-plus/create.
    const project = result.createProject;
    if (!project?._id) return;
    cache.updateQuery({ query: FetchProjectById, variables: { id: project._id } }, () => ({
      projectByID: project,
    }));
  },

  saveSegment: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryField(cache, 'segmentsByBookingId');
  },

  reassignParticipantSegments: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryFields(cache, ['segmentsByBookingId', 'bookingSubmissionConnection']);
  },

  updateBookingSegments: (_result: unknown, args: VariablesOf<typeof UpdateBookingSegments>, cache: Cache) => {
    invalidateBookingById(cache, args.bookingId);
    invalidateQueryFields(cache, ['segmentsByBookingId', 'bookingSubmissionConnection', 'bookingSegmentFills']);
  },

  renameBookingSegment: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryFields(cache, ['bookingSegmentFills', 'segmentsByBookingId']);
  },

  // Slots come from the hours and the study's settings, so a save of either changes them.
  saveStudyAvailability: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryField(cache, 'studySlots');
  },

  updateBooking: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryField(cache, 'studySlots');
  },

  createBooking: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryField(cache, 'bookingsByTeam');
  },

  refundBookingUpdated: (
    _result: ResultOf<typeof RefundBookingUpdated>,
    args: VariablesOf<typeof RefundBookingUpdated>,
    cache: Cache,
  ) => {
    invalidateBookingAndCredits(cache, args.booking_id);
  },

  forceCloseBooking: (
    _result: ResultOf<typeof ForceCloseBooking>,
    args: VariablesOf<typeof ForceCloseBooking>,
    cache: Cache,
  ) => {
    invalidateBookingAndCredits(cache, args.booking_id);
  },

  reopenBooking: (_result: ResultOf<typeof ReOpenBooking>, args: VariablesOf<typeof ReOpenBooking>, cache: Cache) => {
    invalidateBookingById(cache, args.booking_id);
    invalidateQueryField(
      cache,
      'bookingSessionsWithParticipants',
      cachedArgs => cachedArgs?.booking_id === args.booking_id,
    );
    invalidateFocusGroupSessions({ cache, bookingId: args.booking_id });
  },

  // The focus-group calendar reads sessions through `focusGroupSessions`, a Query field of its own that
  // no `Booking` answer writes to, so every write that adds, removes, moves or re-seats a session
  // marks those lists stale — and the summary too, when the set of sessions or their times change.
  createSession: (_result: unknown, args: VariablesOf<typeof CreateFocusGroupSessions>, cache: Cache) => {
    invalidateFocusGroupSessions({ cache, bookingId: args.booking_id, withSummary: true });
  },

  deleteSession: (_result: unknown, args: VariablesOf<typeof DeleteFocusGroupSession>, cache: Cache) => {
    invalidateFocusGroupSessions({ cache, bookingId: args.booking_id, withSummary: true });
  },

  updateSession: (_result: unknown, args: VariablesOf<typeof AssignFocusGroupFacilitator>, cache: Cache) => {
    invalidateFocusGroupSessions({ cache, bookingId: args.booking_id, withSummary: true });
  },

  // These name a submission, not a booking, so every study's lists are marked; only the one on screen
  // re-reads.
  submissionInvite: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateFocusGroupSessions({ cache });
  },

  // The result names the invited submissions but carries none of their fields, so their rows re-read.
  inviteToSelfScheduledStudy: (result: ResultOf<typeof SelfScheduledInvite>, _args: unknown, cache: Cache) => {
    for (const submissionId of result.inviteToSelfScheduledStudy.invited) {
      cache.invalidate({ __typename: 'Submission', _id: submissionId });
    }
    invalidateQueryField(cache, 'bookingSubmissionConnection');
  },

  cancelInvite: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateFocusGroupSessions({ cache });
  },

  createSessionsLiveBooking: (
    _result: ResultOf<typeof CreateSessionsLiveBooking>,
    args: VariablesOf<typeof CreateSessionsLiveBooking>,
    cache: Cache,
  ) => {
    invalidateQueryField(
      cache,
      'bookingSessionsWithParticipants',
      cachedArgs => cachedArgs?.booking_id === args.booking_id,
    );
    invalidateFocusGroupSessions({ cache, bookingId: args.booking_id, withSummary: true });
  },

  rescheduleSessionLiveBooking: (
    _result: ResultOf<typeof RescheduleSessionLiveBooking>,
    args: VariablesOf<typeof RescheduleSessionLiveBooking>,
    cache: Cache,
  ) => {
    invalidateQueryField(
      cache,
      'bookingSessionsWithParticipants',
      cachedArgs => cachedArgs?.booking_id === args.booking_id,
    );
    // The participant table reads session times from this query; refetch it too
    // so it updates immediately instead of serving the pre-reschedule cache.
    invalidateQueryField(cache, 'bookingSubmissionConnection');
    invalidateFocusGroupSessions({ cache, bookingId: args.booking_id, withSummary: true });
  },

  revokeParticipantInvitation: (result: ResultOf<typeof RevokeParticipantInvitation>, _args: unknown, cache: Cache) => {
    const bookingId = result.revokeParticipantInvitation?.session?.booking?._id;
    invalidateQueryField(
      cache,
      'bookingSessionsWithParticipants',
      bookingId ? cachedArgs => cachedArgs?.booking_id === bookingId : undefined,
    );
    invalidateFocusGroupSessions({ cache, bookingId: bookingId ?? undefined });
  },

  reInviteParticipantSession: (
    _result: ResultOf<typeof ReInviteParticipantSession>,
    args: VariablesOf<typeof ReInviteParticipantSession>,
    cache: Cache,
  ) => {
    invalidateQueryField(
      cache,
      'bookingSessionsWithParticipants',
      cachedArgs => cachedArgs?.booking_id === args.booking_id,
    );
    invalidateFocusGroupSessions({ cache, bookingId: args.booking_id });
  },

  updateMe: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryField(cache, 'viewer');
  },

  updateTeam: (_result: unknown, _args: unknown, cache: Cache) => {
    invalidateQueryField(cache, 'teamById');
  },

  ...annotationMutationUpdates,
  ...tagMutationUpdates,
};
